<script lang="ts">
  import { ChevronsUpDown, Clock, Plus } from 'lucide-svelte'
  import { vaultStore } from '$lib/stores/vaultStore'
  import { onMount } from 'svelte'
  
  interface RecentVault {
    name: string
    path: string
    lastAccessed: number
  }
  
  // Extract folder name from path
  function getFolderName(path: string): string {
    const cleanPath = path.replace(/[/\\]+$/, '')
    const segments = cleanPath.split(/[/\\]/)
    return segments[segments.length - 1] || path
  }
  
  let isOpen = $state(false)
  let recentVaults = $state<RecentVault[]>([])
  let currentVault = $state<string>('')
  let currentVaultPath = $state<string>('')
  let dropdownEl: HTMLDivElement | null = null
  
  // Load recent vaults from localStorage
  function loadRecentVaults() {
    const stored = localStorage.getItem('korean-grammar-recent-vaults')
    if (stored) {
      try {
        const isElectron = typeof window !== 'undefined' && (window as any).api
        const loaded = JSON.parse(stored)
        
        // Remove duplicates based on environment
        const seen = new Set<string>()
        const deduplicated: RecentVault[] = []
        
        for (const vault of loaded) {
          const folderName = getFolderName(vault.name || vault.path)
          const key = isElectron ? vault.path : folderName
          
          // Skip if we've already seen this vault
          if (!seen.has(key)) {
            seen.add(key)
            deduplicated.push({
              ...vault,
              name: folderName,  // Ensure folder name only
              path: vault.path || folderName  // Ensure path exists
            })
          }
        }
        
        recentVaults = deduplicated
          .sort((a: RecentVault, b: RecentVault) => b.lastAccessed - a.lastAccessed)
          .slice(0, 5) // Keep only 5 most recent
      } catch {
        recentVaults = []
      }
    }
  }
  
  // Save recent vaults to localStorage
  function saveRecentVaults() {
    localStorage.setItem('korean-grammar-recent-vaults', JSON.stringify(recentVaults))
    
    // Notify main process to update menu
    if (window.electronAPI) {
      const vaultPaths = recentVaults.map(v => v.path)
      window.electronAPI.send('update-recent-vaults', vaultPaths)
    }
  }
  
  // Add or update a vault in recent list
  function addToRecent(name: string, path: string) {
    const folderName = getFolderName(name)  // Ensure we only store folder name
    
    // For web, use name as unique identifier since path is same as name
    // For Electron, use path as unique identifier
    const isElectron = typeof window !== 'undefined' && (window as any).api
    const uniqueKey = isElectron ? path : folderName
    
    // Find existing entry - check both path and name to avoid duplicates
    const existing = recentVaults.findIndex(v => 
      v.path === uniqueKey || 
      (v.name === folderName && !isElectron) // In web, also check by name
    )
    
    if (existing !== -1) {
      // Update existing entry
      recentVaults[existing].lastAccessed = Date.now()
      recentVaults[existing].name = folderName
      recentVaults[existing].path = uniqueKey
    } else {
      // Add new entry
      recentVaults.push({
        name: folderName,
        path: uniqueKey,
        lastAccessed: Date.now()
      })
    }
    
    // Remove duplicates by name for web environment
    if (!isElectron) {
      const seen = new Set<string>()
      recentVaults = recentVaults.filter(v => {
        if (seen.has(v.name)) {
          return false
        }
        seen.add(v.name)
        return true
      })
    }
    
    recentVaults = recentVaults
      .sort((a, b) => b.lastAccessed - a.lastAccessed)
      .slice(0, 5)
    saveRecentVaults()
  }
  
  // Select a vault by path (for restoration)
  async function selectRecentVault(vaultPath: string) {
    const isElectron = typeof window !== 'undefined' && (window as any).api
    
    if (isElectron) {
      // In Electron, open by path
      try {
        const result = await (window as any).api.openVault(vaultPath)
        if (result && result.success && result.dirHandle) {
          await vaultStore.setVault(result.dirHandle)
          const folderName = getFolderName(vaultPath)
          addToRecent(folderName, vaultPath)
          return true
        }
      } catch (err) {
        console.error('Failed to open vault:', err)
      }
    }
    return false
  }
  
  // Open vault picker
  async function openNewVault() {
    isOpen = false

    // Check if we're in Electron
    const isElectron = typeof window !== 'undefined' && (window as any).api

    if (isElectron) {
      // Open vault selection in new window
      const result = await (window as any).api.openVaultInNewWindow()
      if (!result.success) {
        console.error('Failed to open new window:', result.error)
      }
    } else {
      // Web File System API - still use current window
      try {
        const dirHandle = await window.showDirectoryPicker({
          mode: 'readwrite',
          startIn: 'documents'
        })
        await vaultStore.setVault(dirHandle)
        // For web, use folder name as both name and path (no real path available)
        const folderName = getFolderName(dirHandle.name)
        addToRecent(folderName, folderName)
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('Failed to select vault:', err)
        }
      }
    }
  }
  
  // Switch to a recent vault
  async function switchToVault(vault: RecentVault) {
    isOpen = false

    const isElectron = typeof window !== 'undefined' && (window as any).api

    if (isElectron) {
      // In Electron, open vault in new window
      try {
        const result = await (window as any).api.openVaultInNewWindow(vault.path)
        if (!result.success) {
          // If failed, remove from recent and show error
          recentVaults = recentVaults.filter(v => v.path !== vault.path)
          saveRecentVaults()
          alert('해당 Vault를 열 수 없습니다. 다시 선택해주세요.')
        }
      } catch (err) {
        console.error('Failed to open vault in new window:', err)
        recentVaults = recentVaults.filter(v => v.path !== vault.path)
        saveRecentVaults()
        alert('해당 Vault를 열 수 없습니다. 다시 선택해주세요.')
      }
    } else {
      // For web, we can't directly open by path, need to use picker
      alert('이전 Vault로 전환하려면 다시 선택해주세요.')
      openNewVault()
    }
  }
  
  // Handle click outside
  function handleClickOutside(event: MouseEvent) {
    if (dropdownEl && !dropdownEl.contains(event.target as Node)) {
      isOpen = false
    }
  }
  
  onMount(async () => {
    loadRecentVaults()
    
    // Check for pending vault restoration from page reload
    const pendingVault = sessionStorage.getItem('pendingVaultRestore')
    if (pendingVault) {
      sessionStorage.removeItem('pendingVaultRestore')
      try {
        const vaultInfo = JSON.parse(pendingVault)
        if (vaultInfo.isElectron && vaultInfo.path) {
          // Electron - restore by path
          await selectRecentVault(vaultInfo.path)
        } else if (!vaultInfo.isElectron && vaultInfo.name) {
          // Web - try to restore (but may need user to select again due to permissions)
          console.log('Web vault restoration requires manual selection due to browser security')
          // Could show a notification to the user here
        }
      } catch (e) {
        console.error('Failed to restore vault:', e)
      }
    } else {
      // Try to restore from localStorage if no pending restore
      const savedVault = localStorage.getItem('selectedVault')
      if (savedVault) {
        try {
          const vaultInfo = JSON.parse(savedVault)
          if (vaultInfo.isElectron && vaultInfo.path) {
            // Auto-restore Electron vault
            await selectRecentVault(vaultInfo.path)
          }
          // Web vaults cannot be auto-restored due to permissions
        } catch (e) {
          console.error('Failed to auto-restore vault:', e)
        }
      }
    }
    
    // Send initial recent vaults to menu on component mount
    const saved = localStorage.getItem('korean-grammar-recent-vaults')
    if (saved && window.electronAPI) {
      try {
        const vaults = JSON.parse(saved)
        const vaultPaths = vaults.map((v: any) => v.path || v)
        window.electronAPI.send('update-recent-vaults', vaultPaths)
      } catch (e) {
        console.error('Failed to send initial recent vaults:', e)
      }
    }
    
    // Subscribe to vault changes
    const unsubscribe = vaultStore.subscribe(state => {
      currentVault = state.vaultName ? getFolderName(state.vaultName) : 'Vault 선택'
      currentVaultPath = state.vaultPath || ''
      // Don't add to recent here - it's already added when vault is selected
      // This prevents duplicates from being added on every vault state update
    })
    
    // Listen for vault:clear-recent from menu
    if (window.electronAPI && window.electronAPI.on) {
      window.electronAPI.on('vault:clear-recent', () => {
        recentVaults = []
        saveRecentVaults()
      })
      
      // Listen for vault:open-recent from menu
      window.electronAPI.on('vault:open-recent', async (vaultPath: string) => {
        await selectRecentVault(vaultPath)
      })
    }
    
    document.addEventListener('click', handleClickOutside)
    
    return () => {
      unsubscribe()
      document.removeEventListener('click', handleClickOutside)
    }
  })
</script>

<div class="vault-dropdown" bind:this={dropdownEl}>
  <button
    class="vault-trigger"
    onclick={() => isOpen = !isOpen}
    aria-expanded={isOpen}
  >
    <ChevronsUpDown size={14} class="vault-icon" />
    <span class="vault-name">{currentVault}</span>
  </button>
  
  {#if isOpen}
    {@const filteredVaults = recentVaults.filter(v => v.path !== currentVaultPath)}
    <div class="vault-menu">
      <button
        class="vault-item"
        onclick={openNewVault}
      >
        <Plus size={14} />
        <span>새 Vault 열기</span>
      </button>
      
      {#if filteredVaults.length > 0}
        <div class="vault-separator"></div>
        <div class="vault-section-title">최근 사용</div>
        {#each filteredVaults as vault}
          <button
            class="vault-item"
            onclick={() => switchToVault(vault)}
            title={vault.path}
          >
            <Clock size={14} />
            <span class="vault-item-name">{vault.name}</span>
          </button>
        {/each}
      {/if}
    </div>
  {/if}
</div>

<style>
  .vault-dropdown {
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
  }
  
  .vault-trigger {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 4px 4px 0; /* Reduced left padding for closer left alignment */
    width: 100%;
    background: transparent;
    border: none;
    color: var(--text-secondary);
    font-size: var(--ui-font-size-sm);
    cursor: pointer;
    transition: color 0.2s;
    justify-content: flex-start;
  }
  
  .vault-trigger:hover {
    color: var(--text-primary);
  }
  
  .vault-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  :global(.vault-icon) {
    flex-shrink: 0;
    opacity: 0.6;
  }
  
  .vault-trigger:hover :global(.vault-icon) {
    opacity: 1;
  }
  
  .vault-menu {
    position: absolute;
    bottom: 100%;
    left: 0;
    min-width: 200px;
    max-width: 250px;
    background: var(--bg-primary);
    border: 1px solid var(--border-primary);
    border-radius: 6px;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
    padding: 4px;
    margin-bottom: 4px;
    z-index: 1000;
  }
  
  .vault-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 8px;
    background: transparent;
    border: none;
    border-radius: 4px;
    color: var(--text-secondary);
    font-size: var(--ui-font-size-sm);
    text-align: left;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .vault-item:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
  
  .vault-item-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }
  
  .vault-separator {
    height: 1px;
    background: var(--border-primary);
    margin: 4px 0;
  }
  
  .vault-section-title {
    font-size: var(--ui-font-size-xs);
    color: var(--text-tertiary);
    padding: 4px 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
</style>